/**
 * Hook for managing market data with automatic caching via Zustand store
 * Integrates InfoClient with the centralized market store
 */

import { useCallback, useEffect, useRef } from "react";
import { InfoClient } from "../sdk";
import { useMarketStore } from "../store";
import type {
  Market,
  OrderbookData,
  TickerData,
  RecentTrade,
} from "../store/types";
import type { RawMarket, RawTrade } from "../sdk/types/api";

export interface UseMarketsConfig {
  baseURL: string;
  sessionToken?: string;
  autoFetchMarkets?: boolean; // Auto-fetch markets list on mount
}

/**
 * Transform RawMarket to Market interface
 * Converts API response format to Market format with computed fields
 */
function transformRawMarketToMarket(rawMarket: RawMarket): Market {
  const id = rawMarket.market_id;

  return {
    ...rawMarket, // Spread first to preserve all original fields
    id,
    symbol: rawMarket.moniker,
    baseAsset: rawMarket.base_asset,
    quoteAsset: rawMarket.quote_asset,
    active: rawMarket.status === "active",
    maxSizeLimit: 40,
    minSizeLimit: 0.0001,
  };
}

/**
 * Transform RawTrade to RecentTrade
 * Converts API response format to RecentTrade format
 */
function transformRawTradeToRecentTrade(
  rawTrade: RawTrade,
  marketId: string,
): RecentTrade {
  // Convert timestamp from string to number
  const timestamp =
    typeof rawTrade.timestamp === "string"
      ? parseInt(rawTrade.timestamp, 10) || Date.now()
      : typeof rawTrade.timestamp === "number"
        ? rawTrade.timestamp
        : Date.now();

  // Determine side - RawTrade doesn't have a direct side field
  // We can infer it from trade_type or use a default
  // For now, we'll use "buy" as default, but this might need adjustment based on actual API behavior
  const side: "buy" | "sell" =
    rawTrade.trade_type?.toLowerCase().includes("buy") ||
    rawTrade.buy_order_id
      ? "buy"
      : rawTrade.trade_type?.toLowerCase().includes("sell") ||
          rawTrade.sell_order_id
        ? "sell"
        : "buy"; // Default to "buy" if cannot determine

  return {
    // Preserve original fields first
    ...rawTrade,
    // Override with transformed fields
    id: rawTrade.trade_id,
    marketId: rawTrade.market_id || marketId,
    price: rawTrade.price,
    size: rawTrade.quantity,
    side,
    timestamp,
  };
}

/**
 * Hook for centralized market data management
 *
 * @example
 * ```tsx
 * const { markets, getMarket, fetchMarkets, fetchOrderbook } = useMarkets({
 *   baseURL: 'https://router.morpheum.io',
 *   autoFetchMarkets: true,
 * });
 * ```
 */
export function useMarkets(config: UseMarketsConfig) {
  const infoClientRef = useRef<InfoClient | null>(null);

  // Store state and actions
  const markets = useMarketStore((state) => state.markets);
  const marketsById = useMarketStore((state) => state.marketsById);
  const orderbooks = useMarketStore((state) => state.orderbooks);
  const tickers = useMarketStore((state) => state.tickers);
  const recentTrades = useMarketStore((state) => state.recentTrades);
  const selectedMarketId = useMarketStore((state) => state.selectedMarketId);
  const loadingMarkets = useMarketStore((state) => state.loadingMarkets);
  const loadingOrderbooks = useMarketStore((state) => state.loadingOrderbooks);
  const loadingTickers = useMarketStore((state) => state.loadingTickers);
  const loadingTrades = useMarketStore((state) => state.loadingTrades);
  const errors = useMarketStore((state) => state.errors);

  const setMarkets = useMarketStore((state) => state.setMarkets);
  const setMarket = useMarketStore((state) => state.setMarket);
  const getMarket = useMarketStore((state) => state.getMarket);
  const getMarketBySymbol = useMarketStore((state) => state.getMarketBySymbol);
  const setSelectedMarketId = useMarketStore(
    (state) => state.setSelectedMarketId,
  );
  const setOrderbook = useMarketStore((state) => state.setOrderbook);
  const getOrderbook = useMarketStore((state) => state.getOrderbook);
  const updateOrderbook = useMarketStore((state) => state.updateOrderbook);
  const setTicker = useMarketStore((state) => state.setTicker);
  const getTicker = useMarketStore((state) => state.getTicker);
  const setRecentTrades = useMarketStore((state) => state.setRecentTrades);
  const getRecentTrades = useMarketStore((state) => state.getRecentTrades);
  const setLoading = useMarketStore((state) => state.setLoading);
  const setError = useMarketStore((state) => state.setError);
  const clearError = useMarketStore((state) => state.clearError);
  const clearCache = useMarketStore((state) => state.clearCache);

  // Initialize InfoClient
  useEffect(() => {
    if (!infoClientRef.current) {
      infoClientRef.current = new InfoClient({
        baseURL: config.baseURL,
        sessionToken: config.sessionToken,
      });
    } else if (config.sessionToken) {
      infoClientRef.current.setSessionToken(config.sessionToken);
    }
  }, [config.baseURL, config.sessionToken]);

  // Fetch all markets
  const fetchMarkets = useCallback(async (): Promise<Market[]> => {
    if (!infoClientRef.current) {
      throw new Error("InfoClient not initialized");
    }

    const key = "markets";
    setLoading(key, true);
    setError(key, null);

    try {
      const data = await infoClientRef.current.getMarkets();
      const rawMarkets = Array.isArray(data) ? data : [];

      // Normalize API response to Market interface
      // API returns: market_id (string), status ("active"), moniker, base_asset, quote_asset, market_type, orderbook_type, shard_id, activated_at, created_at
      // Market interface expects: id (string), active (boolean), symbol, moniker, baseAsset, quoteAsset
      const marketsList: Market[] = rawMarkets.map((rawMarket: RawMarket) => {
        const id = rawMarket.market_id;

        return {
          ...rawMarket, // Spread first to preserve all original fields
          id,
          symbol: rawMarket.moniker,
          baseAsset: rawMarket.base_asset,
          quoteAsset: rawMarket.quote_asset,
          active: rawMarket.status === "active",
          maxSizeLimit: undefined,
          minSizeLimit: undefined,
        };
      });

      setMarkets(marketsList);
      return marketsList;
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : "Failed to fetch markets";
      setError(key, errorMessage);
      throw error;
    } finally {
      setLoading(key, false);
    }
  }, [setMarkets, setLoading, setError]);

  // Fetch single market by ID
  const fetchMarket = useCallback(
    async (marketId: string): Promise<Market | null> => {
      if (!infoClientRef.current) {
        throw new Error("InfoClient not initialized");
      }

      // Check cache first - convert string marketId to number for internal lookup

      const cached = getMarket(marketId);
      if (cached) {
        return cached;
      }

      try {
        const rawMarket = await infoClientRef.current.getMarketByID(marketId);
        if (rawMarket) {
          const market = transformRawMarketToMarket(rawMarket);
          setMarket(market);
          return market;
        }
        return null;
      } catch (error) {
        const errorMessage =
          error instanceof Error
            ? error.message
            : `Failed to fetch market ${marketId}`;
        setError(`market_${marketId}`, errorMessage);
        throw error;
      }
    },
    [getMarket, setMarket, setError],
  );

  // Fetch market by symbol/moniker
  const fetchMarketBySymbol = useCallback(
    async (symbol: string): Promise<Market | null> => {
      if (!infoClientRef.current) {
        throw new Error("InfoClient not initialized");
      }

      // Check cache first
      const cached = getMarketBySymbol(symbol);
      if (cached) {
        return cached;
      }

      try {
        const rawMarket = await infoClientRef.current.getMarketByMoniker(symbol);
        if (rawMarket) {
          const market = transformRawMarketToMarket(rawMarket);
          setMarket(market);
          return market;
        }
        return null;
      } catch (error) {
        const errorMessage =
          error instanceof Error
            ? error.message
            : `Failed to fetch market ${symbol}`;
        setError(`market_${symbol}`, errorMessage);
        throw error;
      }
    },
    [getMarketBySymbol, setMarket, setError],
  );

  // Fetch orderbook
  const fetchOrderbook = useCallback(
    async (
      marketId: string,
      convertToDecimal: boolean = true,
      depth: number = 20,
    ): Promise<OrderbookData | null> => {
      if (!infoClientRef.current) {
        throw new Error("InfoClient not initialized");
      }

      const key = `orderbook_${marketId}`;
      setLoading(key, true);
      setError(key, null);

      try {
        const data = await infoClientRef.current.getOrderbook(
          marketId,
          convertToDecimal,
          depth,
        );
        if (data && (data.bids || data.asks)) {
          // Convert OrderbookEntry[] ([string | number, string | number][]) to [number, number][]
          const convertEntries = (
            entries: Array<[string | number, string | number]>,
          ): [number, number][] => {
            return entries.map(([price, size]) => [
              typeof price === "string" ? parseFloat(price) : price,
              typeof size === "string" ? parseFloat(size) : size,
            ]);
          };

          const orderbook: OrderbookData = {
            bids: convertEntries(data.bids || []),
            asks: convertEntries(data.asks || []),
            timestamp: Date.now(),
          };
          setOrderbook(
            marketId,
            orderbook,
          );
          return orderbook;
        }
        return null;
      } catch (error) {
        const errorMessage =
          error instanceof Error
            ? error.message
            : `Failed to fetch orderbook for market ${marketId}`;
        setError(key, errorMessage);
        throw error;
      } finally {
        setLoading(key, false);
      }
    },
    [setOrderbook, setLoading, setError],
  );

  // Fetch ticker
  const fetchTicker = useCallback(
    async (marketId: string): Promise<TickerData | null> => {
      if (!infoClientRef.current) {
        throw new Error("InfoClient not initialized");
      }

      const key = `ticker_${marketId}`;
      setLoading(key, true);
      setError(key, null);

      try {
        const data = await infoClientRef.current.getMarketStats(marketId);
        if (data) {
          const ticker: TickerData = {
            marketId: marketId,
            ...data,
            timestamp: Date.now(),
          };
          setTicker(marketId, ticker);
          return ticker;
        }
        return null;
      } catch (error) {
        const errorMessage =
          error instanceof Error
            ? error.message
            : `Failed to fetch ticker for market ${marketId}`;
        setError(key, errorMessage);
        throw error;
      } finally {
        setLoading(key, false);
      }
    },
    [setTicker, setLoading, setError],
  );


  // Fetch recent trades
  const fetchRecentTrades = useCallback(
    async (
      marketId: string,
      limit: number = 100,
    ): Promise<RecentTrade[] | null> => {
      if (!infoClientRef.current) {
        throw new Error("InfoClient not initialized");
      }

      const key = `trades_${marketId}`;
      setLoading(key, true);
      setError(key, null);

      try {
        const data = await infoClientRef.current.getRecentTrades(
          marketId,
          limit,
        );
        if (data) {
          const rawTrades = Array.isArray(data) ? data : [];
          const trades: RecentTrade[] = rawTrades.map((rawTrade) =>
            transformRawTradeToRecentTrade(rawTrade, marketId),
          );
          setRecentTrades(marketId, trades);
          return trades;
        }
        return null;
      } catch (error) {
        const errorMessage =
          error instanceof Error
            ? error.message
            : `Failed to fetch trades for market ${marketId}`;
        setError(key, errorMessage);
        throw error;
      } finally {
        setLoading(key, false);
      }
    },
    [setRecentTrades, setLoading, setError],
  );

  // Auto-fetch markets on mount if enabled
  useEffect(() => {
    if (config.autoFetchMarkets && markets.length === 0 && !loadingMarkets) {
      fetchMarkets().catch(console.error);
    }
  }, [config.autoFetchMarkets, markets.length, loadingMarkets, fetchMarkets]);

  // Find selected market and derive symbol
  const selectedMarket = selectedMarketId
    ? markets.find(
        (m) =>
          m.market_id === selectedMarketId ||
          m.id?.toString() === selectedMarketId,
      )
    : null;

  // Derive market symbol: moniker > symbol > baseAsset/quoteAsset > null
  const selectedMarketSymbol = selectedMarket
    ? selectedMarket.moniker ||
      selectedMarket.symbol ||
      (selectedMarket.baseAsset && selectedMarket.quoteAsset
        ? `${selectedMarket.baseAsset}-${selectedMarket.quoteAsset}`
        : null)
    : null;

  // Get symbol by market ID (from cache)
  const getSymbolByMarketId = useCallback(
    (marketId: string): string | null => {
      // Try to find market by market_id (string) or id (number)
      const market = markets.find(
        (m) => m.market_id === marketId || m.id?.toString() === marketId,
      );

      if (!market) {
        return null;
      }

      // Return symbol in priority: moniker > symbol > baseAsset/quoteAsset
      return (
        market.moniker ||
        market.symbol ||
        (market.baseAsset && market.quoteAsset
          ? `${market.baseAsset}/${market.quoteAsset}`
          : null)
      );
    },
    [markets],
  );

  return {
    // Data
    markets,
    marketsById,
    orderbooks,
    tickers,
    recentTrades,
    selectedMarketId,
    selectedMarket,
    selectedMarketSymbol,

    // Loading states
    loadingMarkets,
    loadingOrderbooks,
    loadingTickers,
    loadingTrades,
    isLoading: (key: string) => {
      if (key === "markets") return loadingMarkets;
      if (key.startsWith("orderbook_")) {
        const marketId = key.split("_").slice(1).join("_"); // Get everything after 'orderbook_'
        return loadingOrderbooks.has(marketId);
      }
      if (key.startsWith("ticker_")) {
        const marketId = key.split("_").slice(1).join("_"); // Get everything after 'ticker_'
        return loadingTickers.has(marketId);
      }
      if (key.startsWith("trades_")) {
        const marketId = key.split("_").slice(1).join("_"); // Get everything after 'trades_'
        return loadingTrades.has(marketId);
      }
      return false;
    },

    // Errors
    errors,
    getError: (key: string) => errors.get(key),

    // Getters (from cache)
    getMarket,
    getMarketBySymbol,
    getSymbolByMarketId,
    getOrderbook,
    getTicker,
    getRecentTrades,

    // Fetchers (from API)
    fetchMarkets,
    fetchMarket,
    fetchMarketBySymbol,
    fetchOrderbook,
    fetchTicker,
    fetchRecentTrades,

    // Store actions
    updateOrderbook,
    clearCache,
    clearError,
    setSelectedMarketId,

    // Direct access to InfoClient if needed
    infoClient: infoClientRef.current,
  };
}
